import { useState, useEffect, useCallback, useRef } from "react";
import api from "../api/client";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import ChatModal from "../components/modals/ChatModal";
import MigrationChatModal from "../components/modals/MigrationChatModal";

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(isoStr).toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "new_installation", label: "New Installation" },
  { value: "migration", label: "Migration" },
];

export default function MessageCenterPage() {
  const { user, hasPerm, isMobileRole } = useAuth();
  const isMobile = useIsMobile();
  const Nav = (isMobile || isMobileRole) ? MobileNavbar : Navbar;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [chatJoId, setChatJoId] = useState(null);
  const [chatMigId, setChatMigId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const searchTimeout = useRef(null);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, filter };
      if (search) params.search = search;
      const { data } = await api.get("/notifications/inbox", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filter, search]);

  useEffect(() => {
    fetchInbox();
  }, [fetchInbox]);

  // Debounced search
  const handleSearchChange = (val) => {
    setSearchInput(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 400);
  };

  const handleFilterChange = (val) => {
    setFilter(val);
    setPage(1);
    setSelected(new Set());
  };

  // Selection helpers
  const toggleSelect = (item) => {
    const key = `${item.source}-${item.record_id}`;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(items.map((i) => `${i.source}-${i.record_id}`)));
  };

  const clearSelection = () => setSelected(new Set());

  const isSelected = (item) => selected.has(`${item.source}-${item.record_id}`);

  const getSelectedItems = () => {
    return items.filter((i) => selected.has(`${i.source}-${i.record_id}`)).map((i) => ({
      source: i.source,
      record_id: i.record_id,
    }));
  };

  // Bulk mark as read
  const markSelectedRead = async () => {
    const sel = getSelectedItems();
    if (!sel.length) return;
    setActionLoading(true);
    try {
      await api.post("/notifications/mark-read", { items: sel });
      setSelected(new Set());
      fetchInbox();
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk mark as unread
  const markSelectedUnread = async () => {
    const sel = getSelectedItems();
    if (!sel.length) return;
    setActionLoading(true);
    try {
      await api.post("/notifications/mark-unread", { items: sel });
      setSelected(new Set());
      fetchInbox();
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  // Open chat
  const openConversation = (item) => {
    if (item.source === "migration") {
      setChatMigId(item.record_id);
    } else {
      setChatJoId(item.record_id);
    }
  };

  const handleChatClose = useCallback(() => {
    setChatJoId(null);
    setChatMigId(null);
    fetchInbox();
  }, [fetchInbox]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      <Nav />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Message Center</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {total} conversation{total !== 1 ? "s" : ""}
                {unreadCount > 0 && (
                  <span className="ml-1.5 text-blue-600 font-medium">{unreadCount} unread</span>
                )}
              </p>
            </div>
            <button
              onClick={fetchInbox}
              disabled={loading}
              className="text-sm px-3 py-1.5 rounded bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 disabled:opacity-50 self-start sm:self-auto"
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {/* Toolbar */}
          <div className="bg-white rounded-t-lg border border-b-0 border-gray-200 px-4 py-3">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="flex-1 relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search JO number, account number, or customer name..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {/* Filters */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {FILTER_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleFilterChange(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      filter === opt.value
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Bulk actions */}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
              <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={items.length > 0 && selected.size === items.length}
                  onChange={() => selected.size === items.length ? clearSelection() : selectAll()}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Select all
              </label>

              {selected.size > 0 && (
                <>
                  <span className="text-xs text-gray-400">|</span>
                  <span className="text-xs text-blue-600 font-medium">{selected.size} selected</span>
                  <button
                    onClick={markSelectedRead}
                    disabled={actionLoading}
                    className="text-xs px-2.5 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50 font-medium"
                  >
                    Mark Read
                  </button>
                  <button
                    onClick={markSelectedUnread}
                    disabled={actionLoading}
                    className="text-xs px-2.5 py-1 rounded bg-yellow-50 text-yellow-700 hover:bg-yellow-100 disabled:opacity-50 font-medium"
                  >
                    Mark Unread
                  </button>
                  <button
                    onClick={clearSelection}
                    className="text-xs px-2.5 py-1 rounded bg-gray-50 text-gray-500 hover:bg-gray-100 font-medium"
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Conversation list */}
          <div className="bg-white border border-gray-200 rounded-b-lg overflow-hidden">
            {loading && items.length === 0 ? (
              <div className="py-16 text-center text-gray-400 text-sm">Loading conversations...</div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-gray-400 text-sm">
                  {search ? "No conversations match your search." : filter === "unread" ? "No unread conversations." : "No conversations yet."}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {items.map((item) => (
                  <div
                    key={`${item.source}-${item.record_id}`}
                    className={`flex items-center gap-3 px-4 py-3 hover:bg-blue-50/50 transition-colors cursor-pointer ${
                      item.is_unread ? "bg-blue-50/30" : ""
                    }`}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={isSelected(item)}
                      onChange={(e) => { e.stopPropagation(); toggleSelect(item); }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                    />

                    {/* Unread dot */}
                    <div className="w-2.5 flex-shrink-0">
                      {item.is_unread && (
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                      )}
                    </div>

                    {/* Content — clickable */}
                    <div
                      className="flex-1 min-w-0"
                      onClick={() => openConversation(item)}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${
                          item.source === "migration"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-blue-100 text-blue-700"
                        }`}>
                          {item.source === "migration" ? "Migration" : "NI"}
                        </span>
                        <span className={`text-sm truncate ${item.is_unread ? "font-semibold text-gray-900" : "font-medium text-gray-700"}`}>
                          {item.jo_number}
                        </span>
                        {item.customer_name && (
                          <span className="text-xs text-gray-500 truncate hidden sm:inline">
                            — {item.customer_name}
                          </span>
                        )}
                        <span className="ml-auto text-[11px] text-gray-400 whitespace-nowrap flex-shrink-0 pl-2">
                          {timeAgo(item.latest_at)}
                        </span>
                      </div>

                      {/* Customer name on mobile (separate line) */}
                      {item.customer_name && (
                        <p className="text-xs text-gray-500 truncate sm:hidden mb-0.5">{item.customer_name}</p>
                      )}

                      {/* Message preview */}
                      {item.latest_message && (
                        <p className={`text-xs truncate ${item.is_unread ? "text-gray-600" : "text-gray-400"}`}>
                          <span className={`${item.is_unread ? "font-medium text-gray-700" : "text-gray-500"}`}>
                            {item.latest_message_by}:
                          </span>{" "}
                          {item.latest_message}
                        </p>
                      )}
                    </div>

                    {/* Message count badge */}
                    <div className="flex-shrink-0 hidden sm:block">
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                        {item.msg_count} msg{item.msg_count !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-1">
              <span className="text-xs text-gray-500">
                Page {page} of {totalPages} ({total} total)
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chat modals — reuse existing */}
      {chatJoId && (
        <ChatModal jobOrderId={chatJoId} onClose={handleChatClose} />
      )}
      {chatMigId && (
        <MigrationChatModal recordId={chatMigId} onClose={handleChatClose} />
      )}
    </>
  );
}
